import { logger } from './logger';

/**
 * Generic pagination response interface that all paginated APIs should follow
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    current_page: number;
    has_next: boolean;
    has_previous: boolean;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

/**
 * Options for configuring pagination behavior
 */
export interface PaginationOptions {
  /**
   * Starting page number (default: 1)
   */
  startPage?: number;
  /**
   * Number of items per page (default: 1000, max: 1000)
   * Higher values reduce the number of API calls but may increase response time per call
   */
  pageSize?: number;
  /**
   * Custom log prefix for better debugging (default: 'data')
   */
  logPrefix?: string;
  /**
   * Maximum number of pages to fetch (default: unlimited)
   * Useful for testing or limiting API calls
   */
  maxPages?: number;
}

/**
 * Configuration for transforming paginated data before returning to client
 */
export interface PaginationTransformerProps<S, T> {
  stage: Record<string, S>;
  fromTarget: (stage: Record<string, S>, data: T[]) => void;
  toTarget: (stage: Record<string, S>) => T[];
}

/**
 * Generic utility function to fetch all paginated data from an API.
 * This function handles pagination automatically by making multiple API calls
 * until all pages are retrieved.
 *
 * @template T - The type of data items in the response
 * @template R - The type of the paginated response (must extend PaginatedResponse<T>)
 *
 * @param fetchPage - Async function that fetches a single page of data
 *                    Takes page number as parameter and returns paginated response
 * @param options - Optional configuration for pagination behavior
 * @param transformProps - Optional configuration for transforming paginated data
 * @returns Promise resolving to array of all data items across all pages
 * @throws Error if any API call fails during pagination
 *
 * @example
 * ```typescript
 * // Fetch all FPN details with pagination
 * const allFPNDetails = await fetchAllPaginatedData<FPNDetails, ListFPNDetails>(
 *   async (page) => {
 *     const result = await fetchGraphQLRequest<{ listFPNDetails: ListFPNDetails }>({
 *       query: listFPNDetails,
 *       variables: { pagination: { page } },
 *       requestName: 'listFPNDetails',
 *       requestType: 'query',
 *     });
 *     return result.listFPNDetails;
 *   },
 *   { logPrefix: 'FPN details' }
 * );
 * ```
 *
 * @example
 * ```typescript
 * // Fetch with custom starting page and max pages limit
 * const limitedData = await fetchAllPaginatedData<User, UserListResponse>(
 *   async (page) => fetchUsersPage(page),
 *   { startPage: 2, maxPages: 5, logPrefix: 'users' }
 * );
 * ```
 */
export const fetchAllPaginatedData = async <T, R extends PaginatedResponse<T>, S = object>(
  fetchPage: (page: number, pageSize: number) => Promise<R>,
  options: PaginationOptions = {},
  transformProps?: PaginationTransformerProps<S, T>
): Promise<T[]> => {
  const { startPage = 1, pageSize = 1000, logPrefix = 'data', maxPages } = options;

  const startTime = performance.now();
  logger.info(`Starting to fetch ${logPrefix} with pagination from page ${startPage} (page size: ${pageSize})`);

  try {
    const allData: T[] = [];
    // Step 1: Fetch the first page synchronously to get pagination metadata
    logger.info(`Fetching ${logPrefix} page ${startPage} (initial request)`);
    const firstPageResponse = await fetchPage(startPage, pageSize);

    // Validate response structure
    if (!firstPageResponse || !firstPageResponse.data) {
      const errorMsg = `Invalid response structure: missing data property on ${logPrefix} page ${startPage}`;
      logger.error(errorMsg);
      throw new Error(errorMsg);
    }

    if (transformProps) {
      transformProps.fromTarget(transformProps.stage, firstPageResponse.data);
    } else {
      allData.push(...firstPageResponse.data);
    }
    const { total_pages, total_count } = firstPageResponse.pagination;

    logger.info(
      `Fetched ${firstPageResponse.data.length} ${logPrefix} from page ${startPage}/${total_pages}. ` +
        `Total accumulated: ${allData.length}/${total_count}`
    );

    // Step 2: If there are more pages, fetch them in parallel
    const remainingPages: number[] = [];
    for (let page = startPage + 1; page <= total_pages; page++) {
      // Check if we've reached the maximum pages limit
      if (maxPages && page - startPage >= maxPages) {
        logger.info(`Reached maximum pages limit (${maxPages}). Stopping pagination.`);
        break;
      }
      remainingPages.push(page);
    }

    if (remainingPages.length > 0) {
      logger.info(`Fetching remaining ${remainingPages.length} pages in parallel: ${remainingPages.join(', ')}`);

      // Create promises for all remaining pages
      const remainingPagePromises = remainingPages.map((page) => fetchPage(page, pageSize));
      // Execute all remaining requests in parallel
      const remainingPageResponses = await Promise.all(remainingPagePromises);
      // Validate and accumulate data from all remaining pages
      remainingPageResponses.forEach((response, index) => {
        const currentPage = remainingPages[index];

        if (!response || !response.data) {
          const errorMsg = `Invalid response structure: missing data property on ${logPrefix} page ${currentPage}`;
          logger.error(errorMsg);
          throw new Error(errorMsg);
        }

        if (transformProps) {
          transformProps.fromTarget(transformProps.stage, response.data);
          logger.info(
            `Fetched ${response.data.length} ${logPrefix} from page ${currentPage}/${total_pages}. ` +
              `Total accumulated records after transform: ${Object.keys(transformProps.stage).length}`
          );
        } else {
          // Add current page's data to accumulated results
          allData.push(...response.data);
          logger.info(
            `Fetched ${response.data.length} ${logPrefix} from page ${currentPage}/${total_pages}. ` +
              `Total accumulated: ${allData.length}/${total_count}`
          );
        }
      });
    }

    if (transformProps) {
      allData.push(...transformProps.toTarget(transformProps.stage));
    }

    const endTime = performance.now();
    const durationMs = endTime - startTime;
    const durationSeconds = (durationMs / 1000).toFixed(2);
    const totalPagesFetched = 1 + remainingPages.length;

    logger.info(
      `Successfully fetched all ${allData.length} ${logPrefix} across ${totalPagesFetched} pages. ` +
        `Total time: ${durationSeconds}s (${durationMs.toFixed(0)}ms). ` +
        `Strategy: first page sync, ${remainingPages.length} pages in parallel`
    );
    return allData;
  } catch (error) {
    logger.error(`Error fetching paginated ${logPrefix}:`, { error });
    throw error;
  }
};

/**
 * Helper function to create a paginated fetch function for GraphQL queries.
 * This simplifies the common pattern of fetching paginated data from GraphQL APIs.
 *
 * @template T - The type of data items in the response
 * @template R - The type of the paginated response
 * @template QueryResult - The type of the GraphQL query result
 *
 * @param fetchGraphQL - Function to execute GraphQL requests
 * @param query - GraphQL query string
 * @param resultKey - Key to access the paginated response in the query result
 * @param requestName - Name of the request for logging
 * @param additionalVariables - Additional variables to include in every request
 *
 * @returns Function that fetches a single page of data
 *
 * @example
 * ```typescript
 * const fetchFPNDetailsPage = createGraphQLPaginatedFetcher<
 *   FPNDetails,
 *   ListFPNDetails,
 *   { listFPNDetails: ListFPNDetails }
 * >(
 *   fetchGraphQLRequest,
 *   listFPNDetails,
 *   'listFPNDetails',
 *   'listFPNDetails'
 * );
 *
 * const allFPNDetails = await fetchAllPaginatedData(
 *   fetchFPNDetailsPage,
 *   { logPrefix: 'FPN details' }
 * );
 * ```
 */
export const createGraphQLPaginatedFetcher = <T, R extends PaginatedResponse<T>, QueryResult>(
  fetchGraphQL: (params: {
    query: string;
    variables: Record<string, unknown>;
    requestName: string;
    requestType: string;
  }) => Promise<QueryResult>,
  query: string,
  resultKey: keyof QueryResult,
  requestName: string,
  additionalVariables: Record<string, unknown> = {}
) => {
  return async (page: number): Promise<R> => {
    const result = await fetchGraphQL({
      query,
      variables: {
        pagination: { page },
        ...additionalVariables,
      },
      requestName,
      requestType: 'query',
    });

    return result[resultKey] as R;
  };
};
